

















































































































































import { Component, Mixins, Prop, Watch } from "vue-property-decorator";
import FabricFilter from "./FabricFilter.vue";
import FabricService from "@/services/fabric_service";
import * as fabric from "@/models/products/fabric";
import { ProductType } from "@/models/products/product";
import Swatch from "@/components/elements/products/Swatch.vue";
import { FabricComponent } from "@/models/configurator/fabric_components";
import {
  CustomChairOrder,
  CustomUmbrellaOrder
} from "@/models/configurator/custom_umbrella";
import { Mobile } from "@/mixins/mobile";
@Component({
  components: {
    FabricFilter,
    Swatch
  }
})
export default class FabricSelector extends Mixins(Mobile) {
  @Prop({ default: false }) visible!: boolean;
  @Prop({ default: FabricComponent.Main }) component!: FabricComponent;
  @Prop() model!: string;
  @Prop() swatch!: fabric.FabricSwatch;
  @Prop() canopy!: CustomUmbrellaOrder["canopy"];
  @Prop() mainFabric!: CustomChairOrder["mainFabric"];
  @Prop() chairBinding!: CustomChairOrder["bindingFabric"];
  @Prop({ default: ProductType.Chair }) product!: ProductType;
  @Prop({ default: null }) index!: null | number;
  /**
   * Externally forced no-stripes flag. Set by the parent when the
   * non-admin user has either a designer trim layout active or a
   * rectangle umbrella selected — both restrictions disallow stripes
   * across all non-fringe/non-binding pickers. Merged with the
   * component-level `noStripes` flag (see `effectiveNoStripes`).
   */
  @Prop({ default: false }) forceNoStripes!: boolean;
  /**
   * When the parent's "custom features" toggle is OFF (stock-only mode),
   * non-Grade A fabrics are hidden from the swatch grid and the
   * Grade filter buttons are disabled (except "Grade A"). Mirrors how
   * the rest of the customizer treats stock-only mode.
   */
  @Prop({ default: false }) isStockOnly!: boolean;
  protected selected: fabric.FabricSwatch | null = null;
  protected fabricService = new FabricService();
  /**
   * Full catalog for the active component, fetched once when the
   * component changes (Fringe → fringe-only, Binding → binding-only,
   * everything else → all fabrics). Grade, color, design, brand,
   * weight, and fabric_type are all client-side filters applied on
   * top of this catalog — there is no per-filter network roundtrip.
   */
  protected unfilteredFabrics: fabric.FabricSwatch[] = [];
  protected searchTerm = "";
  protected fabricComponent = FabricComponent;
  protected solidsOnly = false;
  protected gradeAOnly = false;

  protected get isFringeComponent(): boolean {
    return this.component === FabricComponent.Fringe;
  }

  /**
   * Per-component "no stripes" rule, derived purely from the active
   * `component` and `model`. Computed (not a data field) so it stays
   * in sync when the umbrella model switches mid-session — switching
   * from a rectangle product to a non-rectangle product immediately
   * clears the stripes restriction without needing the picker to
   * re-open.
   *
   * Rules:
   *   - Fringe pickers are always solids-only → no stripes.
   *   - Rectangle umbrellas (model has "-R" suffix) → no stripes.
   *   - Trim / vent-trim / chair-fabric components → no stripes
   *     (stripes don't lay correctly across narrow trim bands).
   */
  protected get noStripes(): boolean {
    if (this.isFringeComponent) return true;
    if (this.isBindingComponent) return true;
    if (this.model && this.model.indexOf("-R") > -1) return true;
    if (
      this.component === FabricComponent.VentTrim ||
      this.component === FabricComponent.MainTrim ||
      this.component === FabricComponent.MainTrimInner ||
      this.component === FabricComponent.ChairFabric ||
      this.component === FabricComponent.Pockets
    ) {
      return true;
    }
    return false;
  }

  /**
   * Merge the per-component `noStripes` rule with the parent-supplied
   * `forceNoStripes` (admin gate + rectangle / trim layout context).
   */
  protected get effectiveNoStripes(): boolean {
    return this.noStripes || this.forceNoStripes;
  }

  protected get isBindingComponent(): boolean {
    return (
      this.component === FabricComponent.Binding ||
      this.component === FabricComponent.ChairBinding
    );
  }
  protected filters: fabric.FabricFilter = {
    grade: ["a"],
    color: [],
    design: "",
    brand: "",
    weight: "",
    fabric_type: ""
  };

  /** Human-readable label for the currently selected grade(s). */
  protected get currentGradeLabel(): string {
    const grades = this.filters.grade;
    if (!grades || grades.length === 0) return "All Grades";
    const labelOne = (g: fabric.FabricGrade): string => {
      if (g === "ap") return "A+";
      if (g === "fp") return "F+";
      if (g === "a") return "A Stock";
      return "Grade " + g.toUpperCase();
    };
    if (grades.length === 1) {
      return grades[0] === "a" || grades[0] === "ap" || grades[0] === "fp"
        ? "Grade " + labelOne(grades[0])
        : labelOne(grades[0]);
    }
    return "Grades " + grades.map(g => labelOne(g)).join(" / ");
  }

  protected get filterMainFabricWeight(): string {
    let weight = "";
    if (this.product === "umbrella" && this.canopy.mainFabric[0]?.weight) {
      weight = this.canopy.mainFabric[0]?.weight;
    } else if (this.product === "chair") {
      weight = "9";
    }
    return weight;
  }

  @Watch("swatch", { deep: true })
  protected onSwatchUpdate(): void {
    this.selected = { ...this.swatch };
    this.filters = this.getFilters(this.component!);
  }
  /** Switcher that executes logic based on the umbrella component we are
   * selecting a fabric for. Watcher on this.component, which changes when
   * the user opens fabric selector modal for a different component slot.
   * Also re-fetches the catalog when the component scope changes
   * (fringe-only / binding-only / all fabrics).
   */
  @Watch("component", { immediate: true })
  protected async onPropUpdate(): Promise<void> {
    // noStripes is now a computed getter derived from `component` + `model`,
    // so it stays in sync automatically. Only the imperative flags
    // (solidsOnly, gradeAOnly) need resetting here.
    this.solidsOnly = false;
    this.gradeAOnly = false;
    if (this.isFringeComponent) {
      this.solidsOnly = true;
    }
    this.filters = this.getFilters(this.component);
    this.unfilteredFabrics = await this.getAllFabrics();
  }

  protected getFilters(
    component: FabricComponent,
    fabric?: fabric.FabricSwatch
  ): fabric.FabricFilter {
    const swatch = fabric ? fabric : this.selected!;
    const filters: fabric.FabricFilter = {
      grade: swatch && swatch.grade ? [swatch.grade] : ["a"],
      color: [],
      design: "",
      brand: "",
      weight: swatch && swatch.weight ? swatch.weight : "",
      fabric_type: ""
    };
    if (
      component === FabricComponent.Binding ||
      component === FabricComponent.ChairBinding
    ) {
      this.setDesign("Solids");
      this.solidsOnly = true;
      // (noStripes is computed from `component` + `model` — no manual
      // assignment needed for Binding / ChairBinding.)
      // Binding fabrics aren't weight-constrained and the Weight filter UI
      // is hidden for this component — clear the seeded weight so WHP
      // (an 8oz fabric) isn't silently filtered out when the previously
      // selected binding fabric is 9oz.
      filters.weight = "";
      // Default the binding grade filter to "All" so users see the full
      // binding catalog across grades rather than being scoped to the
      // currently selected fabric's grade.
      filters.grade = [];
    }
    if (component === FabricComponent.ChairFabric) {
      this.gradeAOnly = true;
    }
    if (component === FabricComponent.Fringe) {
      filters.grade = [];
      this.setDesign("Solids");
      // Fringe fabrics aren't weight-constrained and the Weight filter UI
      // is hidden for this component — clear the seeded weight so WHP
      // (an 8oz fabric) isn't silently filtered out when the previously
      // selected fringe fabric is 9oz.
      filters.weight = "";
    }
    if (component === FabricComponent.Underside) {
      filters.weight = "8";
      filters.grade = [];
    }
    if (component === FabricComponent.Main) {
      // Main canopy can be any weight — default to "All" so the user
      // sees the full catalog instead of being pre-filtered to the
      // currently selected fabric's weight.
      filters.weight = "";
    }
    if (component === FabricComponent.MainAlternating) {
      // Alternating panels must match the primary canopy fabric weight.
      filters.weight = this.filterMainFabricWeight;
    }
    return filters;
  }

  /** Fetches the catalog for the current component — fringe-scoped or
   * binding-scoped when those components are active, otherwise all
   * fabrics across grades. Called once per component activation; grade
   * and the other filters are applied client-side in `filteredFabrics`. */
  protected async getAllFabrics(): Promise<fabric.FabricSwatch[]> {
    const loader = this.$loading.show({
      isFullPage: false,
      container: this.$refs.spinnerContainerFabric
    });
    try {
      const res = await this.fabricService.getAllFabrics({
        isFringe: this.isFringeComponent,
        isBinding: this.isBindingComponent
      });
      return res;
    } catch (err) {
      return [];
    } finally {
      this.$nextTick(() => loader.hide());
    }
  }

  /**
   * Centralized "is this fabric a legitimate option for the active
   * component" rule. Run before any user filters so the eligible pool
   * also drives set-based dimming in FabricFilter.
   *
   * Rules (mirrors shade-designer):
   *   1. Fringe → uses.fringe === true, solids only.
   *   2. Binding → uses.binding === true, solids only.
   *   3. Underside → weight === "8".
   *   4. Non-Main/Binding/Fringe → must match primary canopy fabric weight.
   *   5. Pockets/VentTrim/MainTrim/MainTrimInner → exclude stripes.
   *   6. Inactive / discontinued → always excluded.
   */
  protected getEligiblePool(
    fabrics: fabric.FabricSwatch[]
  ): fabric.FabricSwatch[] {
    const isPattern = (
      swatch: fabric.FabricSwatch,
      pattern: string
    ): boolean => {
      const group = swatch.pattern_group;
      return !!group && group.length > 0 && group.includes(pattern);
    };

    let pool = fabrics.filter(s => !s.inactive && !s.discontinued);

    if (this.isFringeComponent) {
      pool = pool.filter(s => s.uses?.fringe === true);
      pool = pool.filter(s => isPattern(s, "Solids"));
      return pool;
    }

    if (this.isBindingComponent) {
      pool = pool.filter(s => s.uses?.binding === true);
      pool = pool.filter(s => isPattern(s, "Solids"));
      return pool;
    }

    if (this.component === FabricComponent.Underside) {
      pool = pool.filter(s => s.weight === "8");
    } else if (this.component !== FabricComponent.Main) {
      // Every component other than Main / Underside must match the
      // primary canopy fabric weight. This includes MainAlternating
      // (alternating panels must be the same weight as the primary
      // canopy fabric) and trim/vent/valance components. Filtering
      // here also drives set-based dimming so Grade/Color/Design/Brand
      // buttons only highlight options that exist at the locked weight.
      const targetWeight = this.filterMainFabricWeight;
      if (targetWeight) {
        pool = pool.filter(s => s.weight === targetWeight);
      }
    }

    if (
      this.component === FabricComponent.Pockets ||
      this.component === FabricComponent.VentTrim ||
      this.component === FabricComponent.MainTrim ||
      this.component === FabricComponent.MainTrimInner ||
      this.forceNoStripes
    ) {
      pool = pool.filter(s => !isPattern(s, "Stripes"));
    }

    // Stock-only mode: hide all non-Grade A fabrics so the swatch grid
    // mirrors the FabricFilter's disabled non-A grade buttons. This
    // also keeps `availableGrades` consistent (only "a" will appear).
    if (this.isStockOnly) {
      pool = pool.filter(s => s.grade === "a");
    }

    return pool;
  }

  /** The eligible pool before any user-applied filters (used for both
   *  swatch display and to compute available* sets). Built from the
   *  full catalog fetched at mount — grade is applied client-side
   *  later in `filteredFabrics`, not gated server-side. */
  protected get eligiblePool(): fabric.FabricSwatch[] {
    return this.getEligiblePool(this.unfilteredFabrics);
  }

  /** Sets driving FabricFilter button dim state — derived from the
   *  eligible pool so users only see real options. */
  protected get availableGrades(): Set<string> {
    const set = new Set<string>();
    for (const s of this.eligiblePool) if (s.grade) set.add(s.grade);
    return set;
  }
  protected get availableColors(): Set<string> {
    const set = new Set<string>();
    for (const s of this.eligiblePool) {
      if (s.color_group) for (const c of s.color_group) set.add(c);
    }
    return set;
  }
  protected get availableDesigns(): Set<string> {
    const set = new Set<string>();
    for (const s of this.eligiblePool) {
      if (s.pattern_group) for (const d of s.pattern_group) set.add(d);
    }
    return set;
  }
  protected get availableBrands(): Set<string> {
    const set = new Set<string>();
    for (const s of this.eligiblePool) {
      if (s.brand?.handle) set.add(s.brand.handle);
    }
    return set;
  }
  protected get availableWeights(): Set<string> {
    const set = new Set<string>();
    for (const s of this.eligiblePool) if (s.weight) set.add(s.weight);
    return set;
  }

  /**
   * Reorder a swatch array so the WHP default appears first when
   * present. Used as the final step of `filteredFabrics` so that the
   * default fabric is always visible without scrolling.
   */
  protected hoistDefaults(
    fabrics: fabric.FabricSwatch[]
  ): fabric.FabricSwatch[] {
    const whpIdx = fabrics.findIndex(f => f.mfr_code === "WHP");
    if (whpIdx <= 0) return fabrics;
    const whp = fabrics[whpIdx];
    return [whp, ...fabrics.slice(0, whpIdx), ...fabrics.slice(whpIdx + 1)];
  }

  /**
   * Hyphen- and slash-agnostic substring match: both the haystack and
   * the query are lowercased and have `-` and `/` stripped before
   * comparison. So `D020` matches `D-020` and `5615` matches `5/615`.
   */
  protected matchesTerm(haystack: string | undefined, term: string): boolean {
    if (!haystack) return false;
    const normalize = (s: string): string =>
      s.toLowerCase().replace(/[-/]/g, "");
    return normalize(haystack).indexOf(normalize(term)) !== -1;
  }

  /** Getter for filtering fabrics with logic to account for all different
   *  available filters. When a search term is active, searches the eligible
   *  pool (still scoped to the component's catalog) by code/alt-code/name/
   *  frankford_code with hyphen-agnostic matching. */
  protected get filteredFabrics(): fabric.FabricSwatch[] {
    // Global search: scope to the eligible global pool so binding searches
    // stay in binding fabrics, fringe searches stay in fringe fabrics, etc.
    if (this.searchTerm) {
      const term = this.searchTerm.trim();
      const searchPool = this.getEligiblePool(this.unfilteredFabrics);
      const matches = searchPool.filter(s => this.matchesTerm(s.mfr_code, term));
      const matchesAltCode = searchPool.filter(s =>
        this.matchesTerm(s.alt_mfr_code, term)
      );
      const matchesFrankfordCode = searchPool.filter(s =>
        this.matchesTerm(s.frankford_code, term)
      );
      const matchesName = searchPool.filter(s =>
        this.matchesTerm(s.name?.display, term)
      );
      // de-duplicate across match categories (same swatch may match multiple)
      const merged: fabric.FabricSwatch[] = [];
      const seen = new Set<string>();
      for (const swatch of [
        ...matches,
        ...matchesAltCode,
        ...matchesFrankfordCode,
        ...matchesName
      ]) {
        if (!seen.has(swatch._id)) {
          seen.add(swatch._id);
          merged.push(swatch);
        }
      }
      return this.hoistDefaults(merged);
    }

    // No search term: start from the eligible pool, then apply user filters.
    let fabricArray: fabric.FabricSwatch[] = [...this.eligiblePool];

    if (this.component === this.fabricComponent.ChairFabric) {
      this.setWeight(this.filterMainFabricWeight);
    }
    if (this.component === this.fabricComponent.Underside) {
      this.setWeight("8");
    }
    if (this.filters.brand) {
      fabricArray = fabricArray.filter(swatch => {
        return swatch.brand?.handle === this.filters.brand;
      });
    }
    if (this.filters.design) {
      fabricArray = fabricArray.filter(swatch => {
        const pattern: fabric.FabricSwatch["pattern_group"] = swatch.pattern_group!;
        if (pattern && pattern.length > 0) {
          return pattern.includes(this.filters.design);
        } else {
          return false;
        }
      });
    }
    if (this.filters.color.length > 0) {
      const matches: fabric.FabricSwatch[] = [];
      fabricArray.forEach(swatch => {
        const colors: fabric.FabricSwatch["color_group"] = swatch.color_group!;
        if (colors && colors.length > 0) {
          colors.forEach(color => {
            if (this.filters.color.includes(color)) {
              matches.push(swatch);
            }
          });
        }
      });
      fabricArray = matches;
    }
    if (this.effectiveNoStripes) {
      fabricArray = fabricArray.filter(swatch => {
        const pattern: fabric.FabricSwatch["pattern_group"] = swatch.pattern_group!;
        if (pattern && pattern.length > 0 && pattern.includes("Stripes")) {
          return false;
        } else {
          return true;
        }
      });
    }
    if (this.filters.weight) {
      fabricArray = fabricArray.filter(swatch => {
        return swatch.weight === this.filters.weight;
      });
    }
    if (this.filters.fabric_type) {
      fabricArray = fabricArray.filter(swatch => {
        return swatch.fabric_type === this.filters.fabric_type;
      });
    }
    if (this.filters.grade && this.filters.grade.length > 0) {
      const allowed = this.filters.grade;
      fabricArray = fabricArray.filter(swatch => allowed.includes(swatch.grade));
    }
    return this.hoistDefaults(fabricArray);
  }

  protected setFabricType(value: string): void {
    this.filters.fabric_type = value;
  }

  /** Switcher that communicates with umbrella modeler & saves choice to store based on user selections and specific umbrella component */
  protected async select(swatch: fabric.FabricSwatch): Promise<void> {
    if (this.component) {
      this.selected = swatch;
      switch (this.component) {
        case this.fabricComponent.Main:
        case this.fabricComponent.MainAlternating: {
          const fabric = [...this.canopy.mainFabric];
          if (this.index != null) {
            fabric[this.index] = this.selected;
          }
          this.updateFabricSwatch({ mainFabric: fabric });
          break;
        }
        case this.fabricComponent.TopVent: {
          const fabric = [...this.canopy.ventTopFabric];
          if (this.index != null) {
            fabric[this.index] = this.selected;
          }
          this.updateFabricSwatch({ ventTopFabric: fabric });
          break;
        }
        case this.fabricComponent.MiddleVent: {
          const fabric = [...this.canopy.ventMiddleFabric];
          if (this.index != null) {
            fabric[this.index] = this.selected;
          }
          this.updateFabricSwatch({ ventMiddleFabric: fabric });
          break;
        }
        case this.fabricComponent.Binding: {
          this.updateFabricSwatch({ bindingFabric: this.selected });
          break;
        }
        // case this.fabricComponent.Pockets: {
        //   this.updateFabricSwatch({ ribFabric: this.selected });
        //   break;
        // }
        case this.fabricComponent.MainTrim: {
          this.updateFabricSwatch({ mainTrimFabric: this.selected });
          break;
        }
        case this.fabricComponent.MainTrimInner: {
          this.updateFabricSwatch({ mainTrimFabricInner: this.selected });
          break;
        }
        case this.fabricComponent.VentTrim: {
          this.updateFabricSwatch({ ventTrimFabric: this.selected });
          break;
        }
        case this.fabricComponent.Underside: {
          this.updateFabricSwatch({ undersideFabric: this.selected });
          break;
        }
        case this.fabricComponent.Valance: {
          const fabric = [...this.canopy.valanceFabric];
          if (this.index != null) {
            fabric[this.index] = this.selected;
          }
          this.updateFabricSwatch({ valanceFabric: fabric });
          break;
        }
        case this.fabricComponent.Fringe: {
          const fabric = [...this.canopy.fringeFabric];
          if (this.index != null) {
            fabric[this.index] = this.selected;
          }
          this.updateFabricSwatch({ fringeFabric: fabric });
          break;
        }
        case this.fabricComponent.ChairFabric:
          this.updateChairFabricSwatch("mainFabric", this.selected);
          break;
        case this.fabricComponent.ChairBinding:
          this.updateChairFabricSwatch("bindingFabric", this.selected);
          break;
      }
    }
  }

  public updateFabricSwatch(newObj: any) {
    const newFabric = Object.assign({}, { ...this.canopy }, { ...newObj });
    this.$emit("update:canopy", newFabric);
  }

  public updateChairFabricSwatch(field: string, swatch: fabric.FabricSwatch) {
    this.$emit(`update:${field}`, swatch);
  }

  /** set####() methods below set filter values communicated from child FabricFilter.vue */
  /**
   * setGrade accepts the FabricFilter button payload, which can be:
   *   - "" (clear all grades — show all)
   *   - "a/ap" (virtual: select both a and ap)
   *   - any single FabricGrade
   * Grade is purely a client-side filter applied in `filteredFabrics`,
   * so this never triggers a network request.
   */
  protected setGrade(value: string): void {
    if (value === "") {
      this.filters.grade = [];
    } else if (value === "a/ap") {
      this.filters.grade = ["a", "ap"];
    } else {
      this.filters.grade = [value as fabric.FabricGrade];
    }
  }

  protected setColor(values: string[] | []): void {
    this.filters.color = values;
  }

  protected setDesign(value: string): void {
    this.filters.design = value;
  }

  protected setBrand(value: string): void {
    this.filters.brand = value;
  }

  protected setWeight(value: string): void {
    this.filters.weight = value;
  }

  protected hideFabricSelector(): void {
    this.$emit("update:visible", false);
  }
}
